/**
 * Dashboard alert helpers (Phase 8, PA-15).
 * Maps an alert's `action.tab` (a server key such as "authorizations" or "poc") to the chart tab
 * name used by patients.tsx, and decides the alert colour.
 */
import type { DashboardAlert, DashboardAlertSeverity } from '../../../dtos/patientmr/certificationPeriod.dto';

/** Chart tab names (patients.tsx / Sidebar) */
const TAB_BY_KEY: Record<string, string> = {
  authorization: 'Prior Authorizations',
  authorizations: 'Prior Authorizations',
  priorauthorization: 'Prior Authorizations',
  priorauthorizations: 'Prior Authorizations',
  pa: 'Prior Authorizations',
  poc: 'POC Certifications',
  pocs: 'POC Certifications',
  poccertification: 'POC Certifications',
  poccertifications: 'POC Certifications',
  cms485: 'POC Certifications',
  orders: 'Orders',
  order: 'Orders',
  documents: 'Documents',
  document: 'Documents',
  medications: 'Medications',
  demographics: 'Demographics',
  insurance: 'Insurance and Coverage',
  forms: 'Forms',
  careplan: 'Care Plan',
  careplans: 'Care Plan',
  assessments: 'Assessments',
  dashboard: 'Dashboard',
  overview: 'Dashboard',
  certificationperiods: 'Dashboard',
  certifications: 'Dashboard',
  recert: 'Dashboard',
  // Overview v3 next actions (overviewState.js) and Phase 9A schedule alerts
  referrals: 'Referrals',
  referral: 'Referrals',
  team: 'Care Team',
  careteam: 'Care Team',
  schedule: 'Schedule',
  charting: 'Charting',
  visits: 'Visit History',
  // Phase 12: GUARDIAN_* alerts (action.tab 'familyPortal')
  familyportal: 'Family Portal',
  portal: 'Family Portal',
};

const KNOWN_TABS = new Set(Object.values(TAB_BY_KEY));

/** Tab to open for an alert, or null when it has no usable deep link. */
export function alertTargetTab(alert: Pick<DashboardAlert, 'action'>): string | null {
  const raw = alert.action?.tab;
  if (!raw) return null;
  if (KNOWN_TABS.has(raw)) return raw;
  const key = raw.toLowerCase().replace(/[^a-z0-9]/g, '');
  return TAB_BY_KEY[key] ?? null;
}

/** RECERT_OVERDUE is always red; otherwise severity (Phase 8) or type (older alerts). */
export function alertSeverity(alert: Pick<DashboardAlert, 'code' | 'severity' | 'type'>): DashboardAlertSeverity {
  if (alert.code === 'RECERT_OVERDUE' || alert.code === 'PA_EXHAUSTED' || alert.code === 'PA_EXPIRED') return 'ERROR';
  return alert.severity ?? alert.type ?? 'INFO';
}

/** Label for the deep-link button */
export function alertActionLabel(alert: Pick<DashboardAlert, 'code' | 'actionLabel'>, tab: string): string {
  if (alert.actionLabel) return alert.actionLabel;
  switch (alert.code) {
    case 'POC_UNSIGNED':
      return 'Open plan of care';
    case 'RECERT_DUE':
    case 'RECERT_OVERDUE':
      return 'Review recertification';
    case 'PHYSICIAN_VISIT_DUE':
      return 'Open';
    case 'GUARDIAN_INVITE_LOCKED':
      return 'Review locked invite';
    case 'GUARDIAN_PENDING_APPROVAL':
      return 'Approve guardian';
    case 'GUARDIAN_AGE18_REVIEW':
      return 'Review guardian access at 18';
    default:
      return tab === 'Prior Authorizations' ? 'Open authorization' : `Open ${tab}`;
  }
}
